import React, { useCallback, useMemo } from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {useVsiAppContext} from "../../../../stores/VsiApp";
import {VsiBubble, VsiH4, VsiHr} from "../../../../styled/App.styled";
import Grid from "../../../../components/Grid";
import {Card, Spinner} from "reactstrap";
import AtAGlance from "../../../../components/AtAGlance";
import Stockchart from "../../../../components/Stockchart";
import {useDataWithRoute, useTitleFromComponent} from "../../../../utils/hooks";
import {formatStockChartConfig} from "../../../../utils/Helpers";
import {datastoreDefs, datastoreSummaryAag} from "../../../../utils/gridColumnDefs/virtual/datastoreSummary";
import ProgressBar from "../../../../components/ProgressBar";

const chartHeight = 350;

const virtualProgressConfig = [
    {
        type: "single",
        fields: ["pct_used"],
        thresholds: [
            [80, "#008080"],
            [100, "#d80000"],
        ],
        denominator: 100,
        title: "% Used",
    },
    {
        type: "single",
        fields: ["vmdk_alloc_pct_used"],
        thresholds: [
            [80, "#6495ED"],
            [100, "#d80000"],
        ],
        multiply: false,
        title: "% VMDK Used",
        denominator: 100,
    }
];

export default function DatastoreSummary({context, title}) {
    let navigate = useNavigate();
    const {theme, client: { clientId }, periodId} = useVsiAppContext();
    const [searchParams] = useSearchParams();

    const dataStoreName = searchParams.get('datastore_name');
    const vCenterName = searchParams.get('vcenter_name');

    useTitleFromComponent(`${decodeURIComponent(dataStoreName) || 'N/A'} - ${title}`);

    const route = useMemo(() => {
        // seems silly but broken up the way it was, was causing bad characters in the url
        let newRoute = `/api/v6/report/virtual_datastore_summary_proc`;
        newRoute += `?client_id=${clientId}&period_id=${periodId}`;
        newRoute += `&datastore_name=${encodeURIComponent(dataStoreName)}&device_name=${encodeURIComponent(vCenterName)}`;
        return newRoute;
    }, [clientId, periodId, dataStoreName, vCenterName]);

    const [{ recordsets }, {isLoading}] = useDataWithRoute(route);

    const { aagData, trendData } = useMemo(() => {
        let sortedData = (recordsets?.[1] || []).sort((a, b) => new Date(a.collection_date) - new Date(b.collection_date));

        return {
            aagData: recordsets?.[0]?.[0] || {},
            trendData: formatStockChartConfig(sortedData, [
                {
                    name: 'Datastore Size',
                    field: 'datastore_size'
                },
                {
                    name: 'Datastore Used',
                    field: 'datastore_used'
                },
            ]).series,
        }
    }, [recordsets]);

    const onRowDoubleClick = useCallback((e) => {
        // seems silly but broken up the way it was, was causing bad characters in the url
        let route = `/reports/virtual/vm/vm-summary?vm_name=${encodeURIComponent(e.data.vm_name)}`;
        route += `&vcenter_name=${encodeURIComponent(e.data.vcenter_name)}`;
        route += `&cluster_name=${encodeURIComponent(e.data.cluster_name)}`;
        route += `&esx_host_name=${encodeURIComponent(e.data.esx_host_name)}`;
        route += `&period_id=${e.data.period_id}`;
        navigate(route);
    }, [navigate]);

    return (
        <Card className={`report-card p-2 nav-card-${theme}`}>
            <VsiH4>Datastore Summary - {decodeURIComponent(dataStoreName) || 'N/A'}</VsiH4>
            {isLoading ? <Spinner color={"primary"}/> : (
                <div>
                    <VsiBubble className={"d-flex flex-column justify-content-center"}>
                        <AtAGlance config={datastoreSummaryAag} data={aagData} size={"lg"}/>
                    </VsiBubble>
                    <VsiBubble className={"d-flex flex-column justify-content-center my-2"} autobotInfo={{
                        data: recordsets?.[0]?.[0],
                        config: virtualProgressConfig
                    }}>
                        {virtualProgressConfig.map((blueprints, index) => {
                            const data = blueprints.fields.map((field) => aagData?.[field] || 0);
                            return (
                                <ProgressBar
                                    className={"mb-1"}
                                    data={data}
                                    denominator={blueprints.denominator}
                                    title={blueprints.title}
                                    colors={blueprints.colors}
                                    key={`progress-bar-${blueprints.title
                                        .toLowerCase()
                                        .split(" ")
                                        .join("-")}-${index}`}
                                    isMulti={blueprints.type === "multi"}
                                    thresholds={blueprints.thresholds}
                                    multiply={blueprints.multiply}
                                    size={"lg"}
                                />
                            )
                        })}
                    </VsiBubble>

                    <VsiBubble>
                        <Stockchart height={chartHeight}
                                    series={trendData}
                                    saveParams={{
                                        datastoreName: decodeURIComponent(dataStoreName)
                                    }}
                                    navDisabled
                                    yAxisTitle={'GiB'}/>
                    </VsiBubble>
                    <VsiHr/>
                    <VsiBubble>
                        <Grid addStyle
                              data={recordsets?.[2] || []}
                              columnDefs={datastoreDefs}
                              gridText={`Double click to see VM summary`}
                              rowDoubleClicked={onRowDoubleClick}
                        />
                    </VsiBubble>
                </div>
            )}
        </Card>
    )
}
